// src/games/darkforest/encounters.js
import { DIFFICULTY_CLASS } from '../../game-system/skills/skillSystem';

export const encounters = {
  // Skill check encounters
  fallen_tree: {
    id: 'fallen_tree',
    name: 'Fallen Tree Crossing',
    description: 'A massive tree has fallen across the path, blocking your way. You\'ll need to find a way past it.',
    skillChecks: [
      {
        skill: 'athletics',
        difficultyClass: DIFFICULTY_CLASS.MEDIUM,
        description: 'Climb over the fallen tree',
        success: {
          description: 'You successfully climb over the fallen tree and continue on your way.',
          gainExperience: 25,
          nextScene: 'continue_path'
        },
        failure: {
          description: 'You slip while climbing over the tree and take a minor fall.',
          damage: '1d4',
          nextScene: 'continue_path'
        }
      },
      {
        skill: 'acrobatics',
        difficultyClass: DIFFICULTY_CLASS.HARD,
        description: 'Carefully balance and walk across the fallen trunk',
        success: {
          description: 'With careful balance, you walk along the trunk like a tightrope and reach the other side.',
          gainExperience: 35,
          nextScene: 'continue_path'
        },
        failure: {
          description: 'You lose your balance and fall, landing awkwardly.',
          damage: '1d6',
          nextScene: 'continue_path'
        }
      }
    ]
  },
  
  hidden_trail: {
    id: 'hidden_trail',
    name: 'Hidden Trail',
    description: 'The path seems to end here, but there might be a hidden trail continuing through the dense underbrush.',
    skillChecks: [
      {
        skill: 'perception',
        difficultyClass: DIFFICULTY_CLASS.HARD,
        description: 'Search for signs of a hidden trail',
        success: {
          description: 'You notice subtle signs of passage – broken twigs, disturbed soil, and bent grass. There\'s definitely a path here!',
          gainExperience: 30,
          addItem: 'forest_map',
          nextScene: 'hidden_glade'
        },
        failure: {
          description: 'Despite your best efforts, you can\'t find any clear path forward. You might need to double back.',
          nextScene: 'forest_crossroads'
        }
      },
      {
        skill: 'survival',
        difficultyClass: DIFFICULTY_CLASS.MEDIUM,
        description: 'Use wilderness knowledge to find the path',
        success: {
          description: 'Your knowledge of forest travel helps you identify a subtle game trail that seems to lead in the right direction.',
          gainExperience: 25,
          nextScene: 'hidden_glade'
        },
        failure: {
          description: 'You can\'t quite make sense of the forest signs here. The undergrowth is too dense to continue forward.',
          nextScene: 'forest_crossroads'
        }
      }
    ]
  },
  
  mysterious_runes: {
    id: 'mysterious_runes',
    name: 'Mysterious Runes',
    description: 'You discover strange runes carved into a large stone. They glow faintly with an eerie blue light.',
    skillChecks: [
      {
        skill: 'arcana',
        difficultyClass: DIFFICULTY_CLASS.MEDIUM,
        description: 'Attempt to decipher the arcane runes',
        success: {
          description: 'You recognize these as ancient protection runes. They seem to be warding something... or warning of something nearby.',
          gainExperience: 40,
          addItem: 'scroll_identify',
          nextScene: 'rune_path'
        },
        failure: {
          description: 'The runes are beyond your understanding. You feel a strange tingling sensation as you touch them.',
          damage: '1d4',
          damageType: 'arcane',
          nextScene: 'forest_path'
        }
      },
      {
        skill: 'history',
        difficultyClass: DIFFICULTY_CLASS.HARD,
        description: 'Recall historical knowledge about these types of runes',
        success: {
          description: 'You recall these are boundary markers from an ancient elven civilization that once inhabited this forest. They mark the border of a sacred site.',
          gainExperience: 35,
          nextScene: 'ancient_ruins'
        },
        failure: {
          description: 'You know these are very old, but can\'t place their exact origin or purpose.',
          nextScene: 'forest_path'
        }
      }
    ]
  },
  
  quicksand_pit: {
    id: 'quicksand_pit',
    name: 'Treacherous Quicksand',
    description: 'You\'ve stepped into what looked like solid ground, but is actually a treacherous pit of quicksand! You\'re sinking rapidly.',
    skillChecks: [
      {
        skill: 'athletics',
        difficultyClass: DIFFICULTY_CLASS.HARD,
        description: 'Try to swim to the edge',
        success: {
          description: 'With powerful, deliberate swimming motions, you manage to reach solid ground and pull yourself free.',
          gainExperience: 45,
          nextScene: 'forest_path'
        },
        failure: {
          description: 'Your struggle only makes you sink faster! Just as you\'re about to go under, a low-hanging branch allows you to pull yourself free, but not without injury.',
          damage: '2d4',
          nextScene: 'forest_path'
        }
      },
      {
        skill: 'acrobatics',
        difficultyClass: DIFFICULTY_CLASS.VERY_HARD,
        description: 'Attempt to distribute your weight and carefully extract yourself',
        success: {
          description: 'With incredible body control, you manage to spread your weight and slowly work your way to solid ground without sinking further.',
          gainExperience: 60,
          nextScene: 'forest_path'
        },
        failure: {
          description: 'Despite your best efforts, the quicksand is too treacherous. You barely manage to escape, gasping and injured.',
          damage: '2d6',
          nextScene: 'forest_path'
        }
      }
    ]
  },
  
  poisonous_mushrooms: {
    id: 'poisonous_mushrooms',
    name: 'Strange Mushrooms',
    description: 'You find a patch of unusual, colorful mushrooms growing on a fallen log. They might be valuable, but they could also be dangerous.',
    skillChecks: [
      {
        skill: 'nature',
        difficultyClass: DIFFICULTY_CLASS.MEDIUM,
        description: 'Identify the mushrooms',
        success: {
          description: 'You recognize these as Moonlight Caps, a rare fungus with healing properties when properly prepared.',
          gainExperience: 30,
          addItem: 'minor_healing_potion',
          nextScene: 'forest_path'
        },
        failure: {
          description: 'You mistake poisonous Death Caps for edible mushrooms. Handling them gives you a mild rash and irritation.',
          damage: '1d4',
          damageType: 'poison',
          nextScene: 'forest_path'
        }
      },
      {
        skill: 'survival',
        difficultyClass: DIFFICULTY_CLASS.MEDIUM,
        description: 'Carefully harvest the mushrooms',
        success: {
          description: 'You carefully harvest some of the mushrooms, noting which ones appear safest based on their characteristics.',
          gainExperience: 25,
          addItem: 'minor_healing_potion',
          nextScene: 'forest_path'
        },
        failure: {
          description: 'While attempting to harvest the mushrooms, you accidentally release a cloud of toxic spores.',
          damage: '1d6',
          damageType: 'poison',
          nextScene: 'forest_path'
        }
      }
    ]
  },
  
  animal_tracks: {
    id: 'animal_tracks',
    name: 'Fresh Animal Tracks',
    description: 'You notice fresh animal tracks on the forest floor. They appear to belong to something large.',
    skillChecks: [
      {
        skill: 'survival',
        difficultyClass: DIFFICULTY_CLASS.MEDIUM,
        description: 'Track the animal',
        success: {
          description: 'You follow the tracks to a small clearing where you spot a magnificent stag drinking from a stream. It hasn\'t noticed you yet.',
          gainExperience: 25,
          nextScene: 'stag_encounter'
        },
        failure: {
          description: 'You lose the trail after a short distance and find yourself back on the main path.',
          nextScene: 'forest_path'
        }
      },
      {
        skill: 'stealth',
        difficultyClass: DIFFICULTY_CLASS.HARD,
        description: 'Stealthily follow the tracks without alerting the animal',
        success: {
          description: 'Moving silently through the underbrush, you follow the tracks to a small clearing where a magnificent stag is feeding, completely unaware of your presence.',
          gainExperience: 40,
          nextScene: 'stag_encounter_stealth'
        },
        failure: {
          description: 'A snapping twig alerts whatever made the tracks. You hear a sudden crashing through the underbrush as it flees.',
          nextScene: 'forest_path'
        }
      }
    ]
  },
  
  stream_crossing: {
    id: 'stream_crossing',
    name: 'Rapid Stream Crossing',
    description: 'A fast-flowing stream blocks your path. The water isn\'t deep, but the current is strong and the rocks look slippery.',
    skillChecks: [
      {
        skill: 'acrobatics',
        difficultyClass: DIFFICULTY_CLASS.MEDIUM,
        description: 'Hop across using the slippery stones',
        success: {
          description: 'With careful balance, you hop from stone to stone across the stream, staying dry in the process.',
          gainExperience: 25,
          nextScene: 'forest_path'
        },
        failure: {
          description: 'You slip on a moss-covered rock and plunge into the cold water. The current pulls you downstream a short distance before you manage to climb out, soaking wet and a bit bruised.',
          damage: '1d4',
          nextScene: 'stream_bank'
        }
      },
      {
        skill: 'athletics',
        difficultyClass: DIFFICULTY_CLASS.EASY,
        description: 'Wade through the stream',
        success: {
          description: 'You carefully wade through the stream, bracing against the current. Your feet get wet, but you make it across safely.',
          gainExperience: 15,
          nextScene: 'forest_path'
        },
        failure: {
          description: 'The current is stronger than you expected, and you lose your footing. You\'re swept downstream a short way before managing to grab hold of a branch and pull yourself out.',
          damage: '1d6',
          nextScene: 'stream_bank'
        }
      }
    ]
  }
};